import { useEffect, useRef } from "react";
import { openLiveDemoChat } from "@/constants/homeSections";

function scrollToHeroAndRipple() {
  window.scrollTo({ top: 0, behavior: "smooth" });
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("ava-ripple-stores"));
  }, 800);
}

export default function FinalCTA() {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(40px)";
    el.style.transition = "opacity 1s ease, transform 1s ease";

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.style.opacity = "1";
            el.style.transform = "none";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="download"
      className="py-14 md:py-20 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #f5f0ff 0%, #ede8ff 60%, #f0eeff 100%)" }}
    >
      <div ref={contentRef} className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#f5f0ff] border border-[#7C3AED]/20 text-[#7C3AED] text-sm font-medium mb-5">
          <i className="ri-gift-line w-4 h-4 flex items-center justify-center"></i>
          10 free chats — no credit card needed
        </div>

        <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
          Start your first conversation
          <br />
          <span
            className="text-transparent bg-clip-text"
            style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
          >
            Today
          </span>
        </h2>

        <p className="text-[#5a4a8a] text-base max-w-xl mx-auto mb-6">
          Ava is ready whenever you are.
        </p>

        <div className="flex flex-wrap justify-center gap-8 mb-8">
          {[
            { value: "10K+", label: "Conversations" },
            { value: "5★", label: "Rating" },
            { value: "24/7", label: "Available" },
            { value: "10", label: "Free Chats" },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <p className="font-display text-2xl font-bold text-[#2d1b69]">{stat.value}</p>
              <p className="text-[#7a6aaa] text-sm mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-5">
          <button
            type="button"
            className="whitespace-nowrap flex items-center justify-center gap-3 px-8 py-4 rounded-full text-white font-bold text-base cursor-pointer hover:opacity-90 transition-all hover:scale-105"
            style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
            onClick={() => openLiveDemoChat()}
          >
            <i className="ri-chat-smile-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
            Talk to Ava now
          </button>
          <button
            type="button"
            className="whitespace-nowrap flex items-center justify-center gap-3 px-8 py-4 rounded-full border-2 border-[#7C3AED]/40 text-[#2d1b69] font-bold text-base cursor-pointer hover:bg-[#7C3AED]/5 transition-all hover:scale-105"
            onClick={scrollToHeroAndRipple}
          >
            <i className="ri-smartphone-line text-xl w-6 h-6 flex items-center justify-center text-[#7C3AED]"></i>
            Start your first conversation
          </button>
        </div>
        
        <p className="text-[#1a1040] text-lg font-medium mb-2">
          You don't have to carry everything alone anymore.
        </p>

        <p className="text-[#9a8aaa] text-sm">
          Free to download &bull; Privacy-first &bull; Powered by Neuroava
        </p>
      </div>
    </section>
  );
}